import {m} from 'framer-motion';
// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {alpha, useTheme} from '@mui/material/styles';
// components
import Iconify from 'packages/common/components/iconify';
import {RouterLink} from 'packages/common/routes/components';
import {MotionContainer, varFade} from 'packages/common/components/animate';

// ----------------------------------------------------------------------

/**
 * View404 Component
 *
 * Affiche une page d'erreur 404 (Page non trouvée) avec un design moderne et épuré.
 * Utilise une icône de fichier stylisée et des animations subtiles.
 *
 * @return {React.ReactElement} L'élément React représentant la page d'erreur 404.
 */
export default function View404() {
  const theme = useTheme();

  return (
    <MotionContainer>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 4,
        }}
      >
        {/* Icône principale avec cercles décoratifs */}
        <m.div variants={varFade().inUp}>
          <Box
            sx={{
              position: 'relative',
              width: 200,
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
            }}
          >
            {/* Cercle externe animé */}
            <Box
              component={m.div}
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
              }}
            />

            {/* Cercle intermédiaire */}
            <Box
              sx={{
                position: 'absolute',
                width: '75%',
                height: '75%',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.warning.light, 0.08)} 100%)`,
              }}
            />

            {/* Cercle interne avec icône */}
            <Box
              sx={{
                position: 'relative',
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.35)}`,
              }}
            >
              <Iconify
                icon="solar:file-remove-bold"
                width={48}
                sx={{color: 'common.white'}}
              />
            </Box>
          </Box>
        </m.div>

        {/* Code d'erreur */}
        <m.div variants={varFade().inUp}>
          <Typography
            variant="h1"
            sx={{
              fontSize: {xs: 64, sm: 80},
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            404
          </Typography>
        </m.div>

        {/* Titre */}
        <m.div variants={varFade().inUp}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 2,
            }}
          >
            Page introuvable
          </Typography>
        </m.div>

        {/* Description */}
        <m.div variants={varFade().inUp}>
          <Typography
            sx={{
              color: 'text.secondary',
              maxWidth: 400,
              mb: 5,
              lineHeight: 1.7,
            }}
          >
            La page que vous recherchez n&apos;existe pas ou a été déplacée.
            Vérifiez l&apos;URL ou retournez à l&apos;accueil.
          </Typography>
        </m.div>

        {/* Bouton de retour */}
        <m.div variants={varFade().inUp}>
          <Button
            component={RouterLink}
            href="/"
            size="large"
            variant="contained"
            startIcon={<Iconify icon="solar:arrow-left-linear" />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Retour à l&apos;accueil
          </Button>
        </m.div>
      </Box>
    </MotionContainer>
  );
}
